import React, { useState } from 'react'
import { api } from '../lib/api'
import { X, Shield, Key, AlertCircle, CheckCircle } from 'lucide-react'

type Props = {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  isEnabled: boolean
}

export default function TwoFactorSetup({ open, onClose, onSuccess, isEnabled }: Props) {
  const [step, setStep] = useState<'setup' | 'verify' | 'recovery' | 'disable'>('setup')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [manualKey, setManualKey] = useState<string | null>(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [disableCode, setDisableCode] = useState('')

  // Reset state on open/close
  React.useEffect(() => {
    if (open) {
      setStep(isEnabled ? 'disable' : 'setup')
      setError(null)
      setQrCode(null)
      setSecret(null)
      setManualKey(null)
      setVerifyCode('')
      setRecoveryCodes([])
      setDisableCode('')
    }
  }, [open, isEnabled])

  const handleSetup = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.post('/fapi/totp/setup')
      setQrCode(data.qr_code)
      setSecret(data.secret)
      setManualKey(data.manual_entry_key)
      setStep('verify')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleEnable = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setError('Code invalide (6 chiffres requis)')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.post('/fapi/totp/enable', { code: verifyCode })
      setRecoveryCodes(data.recovery_codes)
      setStep('recovery')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Code invalide')
    } finally {
      setLoading(false)
    }
  }

  const handleDisable = async () => {
    if (!disableCode || disableCode.length !== 6) {
      setError('Code invalide (6 chiffres requis)')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await api.post('/fapi/totp/disable', { code: disableCode })
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Code invalide')
    } finally {
      setLoading(false)
    }
  }

  const downloadRecoveryCodes = () => {
    const blob = new Blob([`Codes de récupération TotemAI\n\n${recoveryCodes.join('\n')}`], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'totemAI-recovery-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-800 max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-emerald-600" />
              <h2 className="text-xl font-semibold">
                {isEnabled ? 'Désactiver la 2FA' : 'Double authentification'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* STEP: Setup */}
            {step === 'setup' && (
              <>
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                    <Shield className="w-8 h-8 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Sécurisez votre compte</h3>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      La double authentification ajoute une couche de sécurité supplémentaire à votre compte.
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-900 dark:text-blue-200">
                  <div className="font-medium mb-2">Prérequis :</div>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Google Authenticator</li>
                    <li>Microsoft Authenticator</li>
                    <li>Authy ou toute app TOTP</li>
                  </ul>
                </div>

                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-900 dark:text-red-200 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  onClick={handleSetup}
                  disabled={loading}
                  className="w-full py-3 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Configuration...' : 'Configurer la 2FA'}
                </button>
              </>
            )}

            {/* STEP: Verify */}
            {step === 'verify' && (
              <>
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="font-semibold text-lg mb-2">Scannez le QR code</h3>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      Utilisez votre application d'authentification
                    </p>
                  </div>

                  {qrCode && (
                    <div className="flex justify-center p-4 bg-white rounded-lg">
                      <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48" />
                    </div>
                  )}

                  {manualKey && (
                    <div className="bg-zinc-100 dark:bg-zinc-800 rounded-lg p-4 space-y-2">
                      <div className="text-sm font-medium flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        Clé manuelle :
                      </div>
                      <code className="block text-xs font-mono break-all select-all bg-white dark:bg-zinc-900 p-2 rounded border">
                        {manualKey}
                      </code>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium">
                      Code de vérification (6 chiffres)
                    </label>
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border rounded-lg bg-transparent focus:ring-2 focus:ring-emerald-500"
                      maxLength={6}
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-900 dark:text-red-200 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    onClick={handleEnable}
                    disabled={loading || verifyCode.length !== 6}
                    className="w-full py-3 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Vérification...' : 'Activer la 2FA'}
                  </button>
                </div>
              </>
            )}

            {/* STEP: Recovery Codes */}
            {step === 'recovery' && (
              <>
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">2FA activée avec succès !</h3>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      Sauvegardez ces codes de récupération en lieu sûr.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-900 dark:text-amber-200">
                  <div className="font-medium mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Important !
                  </div>
                  <p>
                    Ces codes vous permettent de vous connecter si vous perdez l'accès à votre application d'authentification.
                  </p>
                </div>

                <div className="bg-zinc-100 dark:bg-zinc-800 rounded-lg p-4 space-y-2">
                  <div className="text-sm font-medium">Codes de récupération :</div>
                  <div className="grid grid-cols-2 gap-2">
                    {recoveryCodes.map((code, i) => (
                      <code key={i} className="text-xs font-mono bg-white dark:bg-zinc-900 p-2 rounded border select-all">
                        {code}
                      </code>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={downloadRecoveryCodes}
                    className="flex-1 py-3 rounded-lg border border-zinc-300 dark:border-zinc-700 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                  >
                    Télécharger
                  </button>
                  <button
                    onClick={() => {
                      onSuccess()
                      onClose()
                    }}
                    className="flex-1 py-3 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors"
                  >
                    Terminer
                  </button>
                </div>
              </>
            )}

            {/* STEP: Disable */}
            {step === 'disable' && (
              <>
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="font-semibold text-lg mb-2">Désactiver la 2FA</h3>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      Entrez votre code d'authentification pour confirmer
                    </p>
                  </div>

                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-900 dark:text-amber-200">
                    <div className="font-medium mb-2 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Attention
                    </div>
                    <p>
                      La désactivation de la 2FA réduira la sécurité de votre compte.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium">
                      Code de vérification (6 chiffres)
                    </label>
                    <input
                      type="text"
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border rounded-lg bg-transparent focus:ring-2 focus:ring-red-500"
                      maxLength={6}
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-900 dark:text-red-200 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={onClose}
                      className="flex-1 py-3 rounded-lg border border-zinc-300 dark:border-zinc-700 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleDisable}
                      disabled={loading || disableCode.length !== 6}
                      className="flex-1 py-3 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {loading ? 'Désactivation...' : 'Désactiver'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
